import React, { useEffect, useMemo, useState, FC } from "react";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    Button,
    ToggleButtonGroup,
    ToggleButton,
    Box,
    Switch,
    FormControlLabel,
    Typography,
    Grid
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import {
    setColorSchemeLocal,
    fetchColorScheme,
    persistColorScheme,
    clearColorScheme,
    selectColorSchemeByScope
} from "../../store/colorSchemeSlice";
import {
    ColorCriterion,
    ColorKey,
    shiftColors,
    ColorSchemeDto
} from "./utils/ShiftColors";
import { EmployeeListItemDto } from "../../models/employee/employee";
import { VehicleListItemDto } from "../../models/transport/vehicle";
import { RouteDto } from "../../models/route/route";
import { getVehicles } from "../../services/vehicleService";
import { getRoutes } from "../../services/routeService";
import { showApiError } from "../framework/formUtils";

type Props = {
    open: boolean;
    onClose: () => void;
    employees?: EmployeeListItemDto[];
};

const colorKeys = Object.keys(shiftColors) as ColorKey[];

export const ColorCodingDialog: FC<Props> = ({ open, onClose, employees }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const colorScheme = useAppSelector(selectColorSchemeByScope("workShiftPlanning"));

    const [vehicles, setVehicles] = useState<VehicleListItemDto[]>();
    const [routes, setRoutes] = useState<RouteDto[]>();

    useEffect(() => {
        if (open) {
            dispatch(fetchColorScheme("workShiftPlanning"));
            getVehicles()
                .then(res => setVehicles(res?.data))
                .catch(showApiError);
            getRoutes()
                .then(setRoutes)
                .catch(showApiError);
        }
    }, [open, dispatch]);

    const list = useMemo(() => {
        if (!colorScheme) return [];
        switch (colorScheme.criterion) {
            case ColorCriterion.Employee:
                return (employees ?? []).map(e => ({
                    id: e.id,
                    name: `${e.firstName} ${e.lastName}`
                }));
            case ColorCriterion.Vehicle:
                return (vehicles ?? []).map(v => ({
                    id: v.id,
                    name: `${v.brand} ${v.registerNumber}`
                }));
            case ColorCriterion.Route:
            default:
                return (routes ?? []).map(r => ({
                    id: r.id,
                    name: r.name
                }));
        }
    }, [colorScheme?.criterion, employees, vehicles, routes]);

    const canEnable = useMemo(
        () =>
            !!colorScheme &&
            Object.keys(colorScheme.mappings ?? {}).length > 0,
        [colorScheme]
    );

    const updateScheme = (partial: Partial<ColorSchemeDto>) => {
        if (!colorScheme) return;
        dispatch(setColorSchemeLocal({ ...colorScheme, ...partial }));
    };

    const setCriterion = (_: unknown, value: ColorCriterion) => {
        if (value === null) return;
        updateScheme({ criterion: value });
    };

    const toggleEnabled = (_: unknown, checked: boolean) => {
        updateScheme({ enabled: checked });
    };

    const setDefault = (key: ColorKey) => {
        updateScheme({ defaultColorKey: key });
    };

    const setItemColor = (id: string, key: ColorKey) => {
        if (!colorScheme) return;
        const next = { ...(colorScheme.mappings ?? {}) };
        next[id] = key;
        updateScheme({ mappings: next });
    };

    const removeItemColor = (id: string) => {
        if (!colorScheme) return;
        const next = { ...(colorScheme.mappings ?? {}) };
        delete next[id];
        updateScheme({ mappings: next });
    };

    const handleSave = () => {
        if (!colorScheme) return;
        const payload: ColorSchemeDto = {
            ...colorScheme,
            enabled:
                colorScheme.enabled &&
                Object.keys(colorScheme.mappings ?? {}).length > 0
        };
        dispatch(persistColorScheme(payload));
        onClose();
    };

    const handleReset = () => {
        dispatch(clearColorScheme("workShiftPlanning"));
    };

    const selectedCriterion =
        colorScheme?.criterion ?? ColorCriterion.Employee;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {t("workShiftPlanning.colorScheme.title")}
            </DialogTitle>
            <DialogContent>
                <Box mb={2}>
                    <Typography variant="subtitle2">
                        {t("workShiftPlanning.colorScheme.selectCriterion")}
                    </Typography>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={selectedCriterion}
                        onChange={setCriterion}
                        sx={{ mt: 1 }}
                    >
                        <ToggleButton value={ColorCriterion.Employee}>
                            {t("workShiftPlanning.colorScheme.employees")}
                        </ToggleButton>
                        <ToggleButton value={ColorCriterion.Vehicle}>
                            {t("workShiftPlanning.colorScheme.vehicles")}
                        </ToggleButton>
                        <ToggleButton value={ColorCriterion.Route}>
                            {t("workShiftPlanning.colorScheme.routes")}
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                <Box mb={2}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={
                                    !!colorScheme?.enabled && canEnable
                                }
                                onChange={toggleEnabled}
                                disabled={!canEnable}
                                color="primary"
                            />
                        }
                        label={t("workShiftPlanning.colorScheme.enable")}
                    />
                    {!canEnable && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                        >
                            {t("workShiftPlanning.colorScheme.enableHint")}
                        </Typography>
                    )}
                </Box>

                <Box mb={2}>
                    <Typography variant="subtitle2">
                        {t("workShiftPlanning.colorScheme.defaultColor")}
                    </Typography>
                    <Box
                        display="flex"
                        gap={1}
                        mt={1}
                        flexWrap="wrap"
                    >
                        {colorKeys.map(k => (
                            <Box
                                key={k}
                                title={k}
                                onClick={() => setDefault(k)}
                                sx={{
                                    width: 28,
                                    height: 28,
                                    bgcolor: shiftColors[k].dark,
                                    borderRadius: 0.5,
                                    cursor: "pointer",
                                    outline:
                                        colorScheme?.defaultColorKey === k
                                            ? "2px solid #000"
                                            : "none"
                                }}
                            />
                        ))}
                    </Box>
                </Box>

                <Box mb={1}>
                    <Typography variant="subtitle2">
                        {t("workShiftPlanning.colorScheme.assignColors")}
                    </Typography>
                </Box>

                <Grid container spacing={2}>
                    {list.map(item => {
                        const mapped = colorScheme?.mappings?.[
                            item.id
                        ];
                        return (
                            <Grid
                                item
                                xs={12}
                                sm={6}
                                md={4}
                                key={item.id}
                            >
                                <Box
                                    sx={{
                                        p: 1,
                                        border: "1px solid #eee",
                                        borderRadius: 1,
                                        minHeight: 80,
                                        bgcolor: mapped
                                            ? "#fafafa"
                                            : undefined,
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 1
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 500,
                                            wordBreak: "break-word",
                                            overflowWrap: "anywhere",
                                            mb: 1
                                        }}
                                        title={item.name}
                                    >
                                        {item.name}
                                    </Typography>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                        flexWrap="wrap"
                                    >
                                        <Box
                                            title={
                                                mapped ??
                                                colorScheme?.defaultColorKey
                                            }
                                            sx={{
                                                width: 24,
                                                height: 24,
                                                borderRadius: 0.5,
                                                bgcolor:
                                                    shiftColors[
                                                        (mapped ??
                                                            colorScheme?.defaultColorKey)
                                                    ].dark,
                                                border:
                                                    "1px solid rgba(0,0,0,0.2)"
                                            }}
                                        />
                                        <Box
                                            display="flex"
                                            gap={0.5}
                                            flexWrap="wrap"
                                            width={170}
                                        >
                                            {colorKeys.map(k => (
                                                <Box
                                                    key={k}
                                                    onClick={() =>
                                                        setItemColor(
                                                            item.id,
                                                            k
                                                        )
                                                    }
                                                    title={k}
                                                    sx={{
                                                        width: 16,
                                                        height: 16,
                                                        borderRadius: 0.5,
                                                        bgcolor:
                                                            shiftColors[k]
                                                                .dark,
                                                        cursor: "pointer",
                                                        outline:
                                                            mapped === k
                                                                ? "2px solid #000"
                                                                : "none"
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                        {!!mapped && (
                                            <Button
                                                size="small"
                                                onClick={() =>
                                                    removeItemColor(item.id)
                                                }
                                            >
                                                {t("common.clear")}
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button
                    color="inherit"
                    onClick={handleReset}
                >
                    {t("workShiftPlanning.colorScheme.reset")}
                </Button>
                <Button onClick={onClose}>
                    {t("workShiftPlanning.colorScheme.cancel")}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                >
                    {t("workShiftPlanning.colorScheme.save")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ColorCodingDialog;